import React, { useEffect, useState } from "react";
import {
  Box,
  MainButton,
  MainCard,
  MainText,
  MainTitle,
  MySafeAreaView,
  PressableOpacity,
} from "app/components/common";
import { Image, Platform, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserFirstName,
  getUserPicture,
} from "@microdoing/shared/services/user/selectors";
import Avatar from "app/scenes/MessageScreen/Avatar";
import {
  getAnalysisType,
  getComment,
  getDonePercentage,
  getIsFeedbackSharedState,
  getRating,
} from "@microdoing/shared/services/action/selectors";
import { validatePerfectStepRequest } from "@microdoing/shared/services/userCampaign/actions";
import analytics from "@react-native-firebase/analytics";
import i18n from "@microdoing/shared/i18n";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import {
  getIsValidateRequesting,
  getStepSelected,
} from "@microdoing/shared/services/userCampaign/selectors";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import CheckBox from "@react-native-community/checkbox";
import { changeActionFeedbackSharedState } from "@microdoing/shared/services/action/actions";
import { getUserCampaignHasPeerToPeer } from "@microdoing/shared/services/userCampaign/selectors";
import SocialSheet from "app/scenes/CurrentTrackScreen/DailyFocusScreen/SelectAction/SocialSheet";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import theme from "app/Theme/Theme";
import HeaderScreen from "app/components/common/HeaderScreen";
import FastImage from "react-native-fast-image";
import { EmptyStarImage, StarImage } from "../ActionRatingScreen/RatingStar";
import normalize from "app/utils/normalize";
import VideoAnalysis from "./VideoAnalysis";
import AudioAnalysis from "./AudioAnalysis";
import TextAndImageAnalysis from "./TextAndImageAnalysis";
import {
  isAndroid,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import WebAnalysisBoxing from "app/components/common/Web/WebAnalysisBoxing";
import Action from "../Action/Action";
import { breadCrumbsReflexiveAnalysisRecapScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { getRate } from "app/utils/getRating";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import PencilIcon from "app/components/Icons/PencilIcon";

const SOCIAL_SHEET_SIZE = normalize(130);

export interface IRecapFeedbackScreenProps {}

const RecapFeedbackScreen = ({ navigation }: IRecapFeedbackScreenProps) => {
  const [width, setWidth] = useState(300);

  const picture = useSelector(getUserPicture);
  const firstName = useSelector(getUserFirstName);
  const rate = useSelector(getRating);
  const donePercentage = useSelector(getDonePercentage);
  const comment = useSelector(getComment);
  const {
    rating,
    totalDone,
    title,
    numberOfComments,
    users,
    content,
  } = useSelector(getStepSelected);
  const language = useSelector(getLanguage);
  const isShared = useSelector(getIsFeedbackSharedState);
  const filesToUpload = useSelector(getFilesToUpload);
  const isValidateRequesting = useSelector(getIsValidateRequesting);
  const analysisType = useSelector(getAnalysisType);
  const hasPeerToPeer = useSelector(getUserCampaignHasPeerToPeer);
  const insets = useSafeAreaInsets();

  const contentTitle = selectLanguage({
    text: content?.title,
    language,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if ((!comment && filesToUpload.length === 0) || !hasPeerToPeer) {
      dispatch(changeActionFeedbackSharedState({ isShared: false }));
    } else {
      dispatch(changeActionFeedbackSharedState({ isShared: true }));
    }
  }, [comment, filesToUpload?.length]);

  const getStars = () => {
    let stars: React.ReactNode[];
    stars = [];
    for (let numberOfStar = 1; numberOfStar <= 5; numberOfStar++) {
      stars.push(
        <FastImage
          key={numberOfStar}
          source={numberOfStar <= rate ? StarImage : EmptyStarImage}
          style={{ height: 27, width: 22 }}
        />,
      );
    }
    return stars;
  };

  const submitAnalysis = () => {
    if (Platform.OS !== "web") {
      analytics().logEvent("validate_action");
    }
    dispatch(validatePerfectStepRequest());
  };

  const getRatingComponent = () => {
    const rate = getRate(donePercentage);

    return (
      <Box flexDirection="row" width="100%" marginTop="xs" alignItems="center">
        <Box width="40%">
          <MainText variant="l3-bold">
            {i18n.t("recap-feedback-feeling")}
          </MainText>
        </Box>
        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
        >
          <MainText marginLeft="xs" variant="l3-medium">
            {rate.name}
          </MainText>
          <Image
            style={{
              marginRight: normalize(-10),
              width: normalize(40),
              height: normalize(40),
            }}
            source={rate.smiley}
          />
        </Box>
      </Box>
    );
  };

  const getPercentageComponent = () => {
    return (
      <Box
        flexDirection="row"
        marginTop="xs"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box width="40%">
          <MainText variant="l3-bold">{i18n.t("recap-feedback-rate")}</MainText>
        </Box>
        <Box
          width={130}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {getStars()}
        </Box>
      </Box>
    );
  };

  const handleChangeIsSharedState = () => {
    dispatch(changeActionFeedbackSharedState({ isShared: !isShared }));
  };

  const findDimensions = ({ width }) => {
    setWidth(width);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  const handleModify = () => {
    navigation.navigate("ReflexiveAnalysisScreen");
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsReflexiveAnalysisRecapScreen}>
      <WebAnalysisBoxing
        leftComponent={
          <MySafeAreaView
            flex={1}
            borderRadius={isWeb ? "inherit" : 0}
            padding={{ smallWeb: "xm", web: "xl", phone: "null" }}
            paddingHorizontal={isWebDesktop ? "webM" : "m"}
            zIndex={0}
            overflow="visible"
            onLayout={isWebDesktop ? handleOnLayout : undefined}
          >
            {isWebDesktop && (
              <MainTitle variant="l6-bold" marginBottom="m">
                {i18n.t("recap-feedback-title")}
              </MainTitle>
            )}
            {!isWebDesktop && (
              <Box paddingTop="xs">
                <HeaderScreen title={i18n.t("recap-feedback-title")} leftIcon />
              </Box>
            )}
            <Box
              flex={1}
              paddingBottom={{ web: "xl" }}
              style={
                !isWebDesktop && {
                  paddingBottom: insets.bottom + theme.spacing.m,
                }
              }
            >
              <MainCard
                variant="cardDiff"
                flex={1}
                onLayout={!isWebDesktop ? handleOnLayout : undefined}
              >
                <Box flex={1}>
                  <Box flexDirection="row" marginBottom="sm">
                    <Box flex={1}>
                      <Box alignItems="center" flexDirection="row">
                        <Avatar
                          picture={picture}
                          styleAvatar={{
                            shadowColor: "transparent",
                          }}
                          size={normalize(24)}
                          isSmall
                        />
                        <MainText marginLeft="xs" variant="l6-bold">
                          {firstName}
                        </MainText>
                      </Box>
                      <MainText
                        marginTop="sm"
                        variant="l3-bold"
                        color="brand.500"
                        numberOfLines={1}
                      >
                        {selectLanguage({ text: title, language }) || `Action`}
                      </MainText>
                    </Box>
                  </Box>
                  <Box marginBottom="sm" flex={1}>
                    {analysisType === "video" && <VideoAnalysis />}
                    {analysisType === "audio" && <AudioAnalysis />}
                    {analysisType === "text" && <TextAndImageAnalysis />}
                    <Box
                      flexDirection="row"
                      justifyContent="space-between"
                      marginTop="sm"
                      alignItems="center"
                    >
                      {(!!comment || filesToUpload.length > 0) &&
                        hasPeerToPeer && (
                          <PressableOpacity onPress={handleChangeIsSharedState}>
                            <Box
                              flexDirection="row"
                              alignItems="center"
                              width="100%"
                            >
                              <CheckBox
                                value={isShared}
                                onChange={
                                  Platform.OS === "android"
                                    ? handleChangeIsSharedState
                                    : () => null
                                }
                                boxType="square"
                                onAnimationType="fill"
                                offAnimationType="fill"
                                tintColor={theme.colors["brand.500"]}
                                onCheckColor={theme.colors["neutral.white"]}
                                onFillColor={theme.colors["brand.500"]}
                                onTintColor={theme.colors["brand.500"]}
                                color={theme.colors["brand.500"]}
                                style={{ width: 16, height: 16 }}
                              />
                              <MainText
                                marginLeft={isAndroid ? "m" : "xs"}
                                variant="l3-medium"
                                color="brand.500"
                              >
                                {i18n.t("publish-comment")}
                              </MainText>
                            </Box>
                          </PressableOpacity>
                        )}
                      <PressableOpacity
                        onPress={handleModify}
                        style={{
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "row",
                        }}
                      >
                        <PencilIcon color={theme.colors["brand.500"]} />
                        <MainText variant="l2-medium" color="brand.500">
                          {i18n.t("modify")}
                        </MainText>
                      </PressableOpacity>
                    </Box>
                  </Box>
                  <View
                    style={{
                      marginLeft: isWebDesktop
                        ? -theme.spacing.xxl
                        : -theme.spacing.xm,
                    }}
                  >
                    <Box
                      width={width}
                      padding="null"
                      borderColor="neutral.100"
                      borderTopWidth={1}
                      marginBottom="xs"
                    />
                  </View>
                  {getRatingComponent()}
                  {getPercentageComponent()}
                </Box>
              </MainCard>
            </Box>

            <Box>
              <MainButton
                isLoading={isValidateRequesting}
                text={i18n.t("submit-analysis")}
                onPress={submitAnalysis}
              />
            </Box>
          </MySafeAreaView>
        }
        rightComponent={<Action />}
      />
    </WebDesktopView>
  );
};

export default RecapFeedbackScreen;
